import React, { useState } from 'react';
import { AlertCircle, Check, Copy, Eye, EyeOff, RotateCcw, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api';
import { theme } from '../config/theme';

type OrganizationOption = {
  name: string;
  slug: string;
};

type TwoFactorMode = 'NONE' | 'VERIFY' | 'ENROLL' | 'RECOVERY_CODES';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [organizationOptions, setOrganizationOptions] = useState<OrganizationOption[]>([]);
  const [organizationSlug, setOrganizationSlug] = useState('');
  const [twoFactorMode, setTwoFactorMode] = useState<TwoFactorMode>('NONE');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [pendingLogin, setPendingLogin] = useState<{ token?: string; user?: unknown } | null>(null);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [enrollmentNotice, setEnrollmentNotice] = useState('');

  const persistLogin = (data: { token?: string; user?: unknown }) => {
    if (!data.token || !data.user) throw new Error('Resposta de login inválida');
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    navigate('/');
  };

  const startEnrollment = async (token: string, resetSecret = false) => {
    const response = await fetch(`${getApiUrl()}/api/auth/2fa/enrollment/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ setupToken: token, resetSecret }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Não foi possível iniciar a autenticação em duas etapas');
    setSetupToken(token);
    setManualKey(data.manualKey || '');
    setTwoFactorCode('');
    setEnrollmentNotice(resetSecret ? 'Nova chave criada. Remova a chave anterior do autenticador e cadastre esta nova chave.' : '');
    setTwoFactorMode('ENROLL');
  };

  const handleCopyManualKey = async () => {
    try {
      await navigator.clipboard.writeText(manualKey);
      setEnrollmentNotice('Chave copiada. Cole-a no aplicativo autenticador.');
    } catch {
      setEnrollmentNotice('Não foi possível copiar automaticamente. Selecione e copie a chave exibida.');
    }
  };

  const handleResetEnrollmentSecret = async () => {
    setError('');
    setLoading(true);
    try {
      await startEnrollment(setupToken, true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível gerar uma nova chave');
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollmentConfirm = async () => {
    const response = await fetch(`${getApiUrl()}/api/auth/2fa/enrollment/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ setupToken, code: twoFactorCode }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Não foi possível validar o código de autenticação');
    if (Array.isArray(data.recoveryCodes) && data.recoveryCodes.length) {
      setRecoveryCodes(data.recoveryCodes);
      setPendingLogin(data);
      setTwoFactorMode('RECOVERY_CODES');
      return;
    }
    persistLogin(data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (twoFactorMode === 'ENROLL') {
        await handleEnrollmentConfirm();
        return;
      }
      if (twoFactorMode === 'RECOVERY_CODES') {
        if (!pendingLogin) throw new Error('Sessão de configuração inválida. Entre novamente.');
        persistLogin(pendingLogin);
        return;
      }
      const res = await fetch(`${getApiUrl()}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          organizationSlug: organizationSlug || undefined,
          twoFactorCode: twoFactorMode === 'VERIFY' && !useRecoveryCode ? twoFactorCode : undefined,
          twoFactorRecoveryCode: twoFactorMode === 'VERIFY' && useRecoveryCode ? twoFactorCode : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (data.requiresOrganizationSelection && Array.isArray(data.organizations)) {
        setOrganizationOptions(data.organizations);
        setOrganizationSlug('');
        setError('');
        return;
      }

      if (data.requiresTwoFactorEnrollment && data.setupToken) {
        setEnrollmentNotice('');
        await startEnrollment(data.setupToken);
        return;
      }

      if (data.requiresTwoFactor) {
        setTwoFactorMode('VERIFY');
        setTwoFactorCode('');
        return;
      }

      if (!res.ok) {
        throw new Error(data.error || 'Erro ao realizar login');
      }

      persistLogin(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao realizar login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-surface dark:bg-surface-dark overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <div className="mb-8">
            <img src="/iasolicitar-logo.svg" alt="IaSolicitar" className="h-14 object-contain" />
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-ink-title dark:text-ink-title-dark mb-1">Bem-vindo de volta</h2>
            <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Faça login para acessar o painel do UltraLicitar</p>
          </div>

          {error && (
            <div className={theme.classes.alert.error + " flex items-start gap-3"}>
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {twoFactorMode === 'RECOVERY_CODES' ? (
              <>
                <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-4 dark:border-amber-800 dark:bg-amber-950/30">
                  <div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 text-amber-700 dark:text-amber-300" /><div><p className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">Códigos de recuperação</p><p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Guarde estes códigos em local seguro. Cada um funciona uma única vez caso o autenticador não esteja disponível.</p></div></div>
                  <div className="mt-3 grid grid-cols-2 gap-2">{recoveryCodes.map((code) => <code key={code} className="rounded border border-amber-200 bg-surface px-2 py-1.5 text-center text-xs font-medium text-ink-title dark:border-amber-800 dark:bg-surface-dark dark:text-ink-title-dark">{code}</code>)}</div>
                </div>
              </>
            ) : twoFactorMode === 'ENROLL' ? (
              <>
                <div className="rounded-lg border border-brand-200 bg-brand-50/60 p-4 dark:border-brand-700/50 dark:bg-brand-950/30">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 text-brand-700 dark:text-brand-300" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">Proteja o acesso administrativo</p>
                      <p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">No autenticador, use TOTP com 6 dígitos, período de 30 segundos e SHA-1. Em seguida, informe o código atual.</p>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <code className="min-w-0 flex-1 break-all rounded border border-line bg-surface px-3 py-2 text-xs text-ink-title dark:border-line-dark dark:bg-surface-dark dark:text-ink-title-dark">{manualKey}</code>
                    <button type="button" onClick={handleCopyManualKey} className={theme.classes.button.secondary + ' shrink-0 px-3'} aria-label="Copiar chave do autenticador" title="Copiar chave"><Copy className="h-4 w-4" /></button>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Conta: seu e-mail. Em caso de erro, gere outra chave e substitua a anterior no autenticador.</p>
                    <button type="button" disabled={loading} onClick={handleResetEnrollmentSecret} className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-brand-700 disabled:opacity-60 dark:text-brand-300"><RotateCcw className="h-3.5 w-3.5" />Nova chave</button>
                  </div>
                  {enrollmentNotice && <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300"><Check className="h-3.5 w-3.5" />{enrollmentNotice}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-label dark:text-ink-label-dark mb-2">Código de autenticação</label>
                  <input autoFocus inputMode="numeric" autoComplete="one-time-code" minLength={6} maxLength={6} value={twoFactorCode} onChange={(event) => setTwoFactorCode(event.target.value.replace(/\D/g, ''))} required placeholder="000000" className={theme.classes.input.base + ' tracking-[0.3em]'} />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-ink-label dark:text-ink-label-dark mb-2">E-mail</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="seuemail@empresa.com" className={theme.classes.input.base} />
                </div>

                {organizationOptions.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-ink-label dark:text-ink-label-dark mb-2">Empresa</label>
                    <select value={organizationSlug} onChange={(event) => setOrganizationSlug(event.target.value)} required className={theme.classes.input.base}>
                      <option value="">Selecione a empresa</option>
                      {organizationOptions.map((organization) => <option key={organization.slug} value={organization.slug}>{organization.name}</option>)}
                    </select>
                    <p className="mt-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Este e-mail possui acesso a mais de uma empresa.</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-ink-label dark:text-ink-label-dark mb-2">Senha</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Digite sua senha" className={theme.classes.input.base + " pr-12"} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark transition-colors" aria-label={showPassword ? 'Ocultar senha' : 'Exibir senha'}>
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {twoFactorMode === 'VERIFY' && (
                  <div>
                    <label className="block text-sm font-medium text-ink-label dark:text-ink-label-dark mb-2">{useRecoveryCode ? 'Código de recuperação' : 'Código de autenticação'}</label>
                    <input autoFocus inputMode={useRecoveryCode ? 'text' : 'numeric'} autoComplete={useRecoveryCode ? 'off' : 'one-time-code'} maxLength={useRecoveryCode ? 9 : 6} value={twoFactorCode} onChange={(event) => setTwoFactorCode(useRecoveryCode ? event.target.value.toUpperCase() : event.target.value.replace(/\D/g, ''))} required placeholder={useRecoveryCode ? 'ABCD-EFGH' : '000000'} className={theme.classes.input.base + (useRecoveryCode ? '' : ' tracking-[0.3em]')} />
                    <button type="button" onClick={() => { setUseRecoveryCode((current) => !current); setTwoFactorCode(''); }} className="mt-2 text-xs font-medium text-brand-700 dark:text-brand-300">{useRecoveryCode ? 'Usar código do autenticador' : 'Usar código de recuperação'}</button>
                  </div>
                )}
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className={theme.classes.button.primary + " w-full py-3.5"}
            >
              {loading ? 'Validando...' : twoFactorMode === 'RECOVERY_CODES' ? 'Concluir acesso seguro' : twoFactorMode === 'ENROLL' ? 'Ativar proteção e entrar' : twoFactorMode === 'VERIFY' ? 'Validar e entrar' : organizationOptions.length > 0 ? 'Entrar na empresa selecionada' : 'Entrar'}
            </button>
          </form>

          <div className="mt-6 text-sm text-ink-subtitle dark:text-ink-subtitle-dark space-y-3">
            <p className="text-center">
              <button
                onClick={() => navigate('/forgot-password')}
                className={theme.classes.link.primary}
              >
                Esqueci minha senha
              </button>
            </p>
            <p className="text-center">
              Ainda não tem empresa cadastrada?{' '}
              <button
                onClick={() => navigate('/register')}
                className={theme.classes.link.primary}
              >
                Criar empresa
              </button>
            </p>
          </div>
        </div>
      </div>

      <div className={`hidden lg:flex lg:w-1/2 bg-gradient-to-br ${theme.colors.gradients.backgroundDark} relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="diagonal-lines-2" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <line x1="0" y1="40" x2="40" y2="0" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#diagonal-lines-2)" />
          </svg>
        </div>

        <div className="relative z-10 flex items-center justify-center w-full p-16">
          <div className="text-center text-white">
            <h2 className="text-5xl font-bold mb-6">Bem-vindo ao UltraLicitar</h2>
            <p className="text-xl opacity-90 mb-8">A plataforma completa para gestão de licitações</p>
            <div className="grid grid-cols-1 gap-6 max-w-md mx-auto text-left">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Gestão Completa</h3>
                  <p className="text-sm opacity-80">Licitações, contratos e notificações em um só lugar</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none">
                    <path d="M3 12h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold mb-1">IA Integrada</h3>
                  <p className="text-sm opacity-80">Análises inteligentes dos editais</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
